import { HTTP_HEADERS } from '../../auth/const';
import { getPublicPageURL } from '../../utils/fetch/hooks/beforeRequest/interface';
import type { QueryMeta } from '../../utils/fetch/types';
import { addTenantPrefix } from '../../utils/tenantBasePath';
import { getPublicApiURL } from '../../utils/url/publicUrl';

const AUTH_TOKEN_KEY = 'uaAuthToken';

/**
 * URL-object-based file URL adaptation for client-side (domain available).
 * constructs a URL object, applies public API/page transforms, attaches auth
 * token and preview params, then prepends the tenant basePath.
 */
export const adaptFileUrlWithDomain = (
  url: string,
  originalUrl: string,
  domain: string,
  queryMeta: QueryMeta | undefined,
  options: { preview?: boolean } = { preview: true },
): string => {
  const isPublicInterface = queryMeta?.isPublicInterface;
  const uaAuthToken =
    queryMeta?.headers?.[HTTP_HEADERS.AuthToken] ||
    queryMeta?.headers?.[HTTP_HEADERS.ClientAuthToken];
  let urlObj: URL;

  try {
    // for contentstack, url is absoulute path and domain is /support so new URL will throw error
    //in order to avoid this, we are checking if the domain starts with / and if it does, we are not adding the domain to the url
    urlObj = domain.startsWith('/') ? new URL(url) : (new URL(url, domain) as URL);
  } catch (e) {
    console.error('error adapting file url', e);
    return originalUrl;
  }

  if (isPublicInterface) {
    urlObj = getPublicApiURL(urlObj, queryMeta);
  }

  // Mirror the ky network hook gating — only anonymous visitors on a public
  // page get the /auth/* rewrite. Logged-in users keep /api/* so their session
  // is honored on direct browser fetches (image/video/citation src).
  if (queryMeta?.isPublicPage && !queryMeta?.isUserLoggedIn) {
    urlObj = getPublicPageURL(urlObj);
  }

  try {
    if (uaAuthToken) {
      // set the auth token to the url
      urlObj.searchParams.set(AUTH_TOKEN_KEY, uaAuthToken);
    }

    if (options?.preview) {
      // add preview param to the url
      // allows previewing the file when accessing the url in the browser
      urlObj.searchParams.set('preview', 'true');
    }

    return addTenantPrefix(urlObj.toString(), queryMeta?.basePath);
  } catch (e) {
    // Malformed URL, return original string or handle as needed
    console.error('error adapting file url', e);
    return addTenantPrefix(url, queryMeta?.basePath);
  }
};
